import React, { useEffect, useState, useCallback } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import { messList, messAttendanceMonthlyReport } from "../../api/Mess";
import { printElement } from "../master/Print";
import MessLayout from "./MessLayout";
import usePrivateLink from "../config/usePrivateLink";

export default function MessAttendanceMonthlySummary() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false);
  const [messes, setMesses] = useState([]);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    month: new Date().toISOString().slice(0, 7),
    type: "Student",
  });

  const getMessList = async () => {
    setLoading(true);
    try {
      const response = await messList(`&limit=all`);
      if (response.data?.status) {
        setMesses(response?.data?.data);
        setFilters((prev) => ({
          ...prev,
          mess_id: response?.data?.data?.[0]?.id,
        }));
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getDataList = useCallback(async () => {
    if (!filters?.mess_id || !filters?.month) return;
    setLoading(true);
    const [year, month] = filters.month.split("-").map(Number);
    try {
      const response = await messAttendanceMonthlyReport({
        mess_id: filters.mess_id,
        type: filters.type || undefined,
        month,
        year,
      });
      if (response.data?.status) {
        setData(response?.data?.data ?? []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    getMessList();
  }, []);

  useEffect(() => {
    getDataList();
  }, [getDataList]);

  const selectedMess = messes?.find((m) => m.id == filters?.mess_id);
  const monthLabel = filters?.month
    ? new Date(`${filters.month}-01`).toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    })
    : "";

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row py-5">
                  <MessLayout />
                  <div className="col-12">
                    <div className="card my-3">
                      <div className="card-body">
                        <h4 className="text-primary mb-0">Monthly Summary</h4>
                        <div className="text-muted small mb-2">
                          Present/absent day counts per person for the month —
                          use this for billing.
                        </div>
                        <hr />
                        <div className="row align-items-center">
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters?.mess_id ?? ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    mess_id: e.target.value,
                                  })
                                }
                              >
                                {messes?.map((mess) => (
                                  <option key={mess.id} value={mess.id}>
                                    {mess.name}
                                  </option>
                                ))}
                              </select>
                              <label>Select Mess</label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={filters?.type ?? ""}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    type: e.target.value,
                                  })
                                }
                              >
                                <option value="">All Types</option>
                                <option value="Student">Student</option>
                                <option value="Staff">Staff</option>
                                <option value="Guest">Guest</option>
                              </select>
                              <label>Type</label>
                            </div>
                          </div>
                          <div className="col-md-3 col-sm-6 mb-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="month"
                                className="form-control"
                                value={filters?.month}
                                onChange={(e) =>
                                  setFilters({
                                    ...filters,
                                    month: e.target.value,
                                  })
                                }
                              />
                              <label>Month</label>
                            </div>
                          </div>
                          <div className="col-md-3">
                            {hasAccess("/mess/print") && (

                              <button
                                className="btn btn-outline-info"
                                onClick={() =>
                                  printElement(
                                    "PrintDivSummary",
                                    `@page { size: A4 portrait; }`,
                                    0.9,
                                  )
                                }
                              >
                                Print
                              </button>
                            )}

                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card">
                      <div className="card-body" id="PrintDivSummary">
                        {selectedMess && (
                          <div className="text-center mb-3">
                            <h4 className="text-primary mb-0">
                              {selectedMess.name}
                            </h4>
                            <div className="text-muted">{monthLabel}</div>
                          </div>
                        )}
                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Mobile</th>
                                <th className="text-center">Present Days</th>
                                <th className="text-center">Absent Days</th>
                                <th className="text-center">Marked Days</th>
                                <th className="text-center">Attendance %</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.map((r, i) => {
                                const pct = r.marked_days
                                  ? Math.round(
                                    (r.present_days / r.marked_days) * 100,
                                  )
                                  : 0;
                                return (
                                  <tr key={r.enrollment_id}>
                                    <td>{i + 1}</td>
                                    <td>{r.enrollment?.name ?? "-"}</td>
                                    <td>{r.enrollment?.type ?? "-"}</td>
                                    <td>{r.enrollment?.mobile ?? "-"}</td>
                                    <td className="text-center text-success fw-bold">
                                      {r.present_days}
                                    </td>
                                    <td className="text-center text-danger fw-bold">
                                      {r.absent_days}
                                    </td>
                                    <td className="text-center">
                                      {r.marked_days}
                                    </td>
                                    <td className="text-center">{pct}%</td>
                                  </tr>
                                );
                              })}
                              {!data.length && (
                                <tr>
                                  <td
                                    colSpan={8}
                                    className="text-center text-muted"
                                  >
                                    No attendance recorded for this month yet.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
